import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonText,
  IonIcon,
  IonFooter,
} from '@ionic/react';
import { banOutline } from 'ionicons/icons';
import { UnpaidWerkerSummary, UnpaidJobData } from '../../types/payment';
import { formatCurrency, formatISODateTime } from '../../utils/paymentCalculations';

interface JobSelectionModalProps {
  isOpen: boolean;
  werkerData: UnpaidWerkerSummary;
  onDismiss: (data?: any) => void;
}

export const JobSelectionModal: React.FC<JobSelectionModalProps> = ({
  isOpen,
  werkerData,
  onDismiss,
}) => {
  const [selectedJobs, setSelectedJobs] = useState<UnpaidJobData[]>([]);
  const [jobs, setJobs] = useState<UnpaidJobData[]>([]);

  // Initialize jobs when modal opens
  useEffect(() => {
    if (isOpen && werkerData) {
      setJobs([...werkerData.unpaidJobs]);
      setSelectedJobs([]);
    }
  }, [isOpen, werkerData]);

  const handleJobToggle = (job: UnpaidJobData) => {
    const isSelected = selectedJobs.some((j) => j.jobDocId === job.jobDocId);
    
    if (isSelected) {
      setSelectedJobs(selectedJobs.filter((j) => j.jobDocId !== job.jobDocId));
    } else {
      setSelectedJobs([...selectedJobs, job]);
    }
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs([...jobs]);
    }
  };

  const handleProceed = () => {
    if (selectedJobs.length === 0) return;
    
    onDismiss({
      selectedJobs: selectedJobs,
    });
  };

  const handleCancel = () => {
    onDismiss();
  };

  const totalSelected = selectedJobs.reduce((sum, job) => sum + job.estimatedAmount, 0);
  const allSelected = selectedJobs.length === jobs.length && jobs.length > 0;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleCancel}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Select Jobs to Pay</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleCancel}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {werkerData && (
          <>
            <div style={{ padding: '16px', borderBottom: '1px solid var(--ion-color-light)' }}>
              <IonText>
                <h2 style={{ margin: '0 0 4px 0' }}>{werkerData.werkerName}</h2>
                <p style={{ margin: 0, color: 'var(--ion-color-medium)' }}>
                  {jobs.length} unpaid job{jobs.length !== 1 ? 's' : ''}
                </p>
              </IonText>
            </div>

            <div style={{ padding: '8px 16px' }}>
              <IonButton
                fill="clear"
                size="small"
                onClick={handleSelectAll}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </IonButton>
            </div>

            <IonList>
              {jobs.map((job) => {
                const isChecked = selectedJobs.some((j) => j.jobDocId === job.jobDocId);

                return (
                  <IonItem key={job.jobDocId} button detail={false} onClick={() => handleJobToggle(job)}>
                    <IonCheckbox
                      slot="start"
                      checked={isChecked}
                      onClick={(e) => { e.stopPropagation(); handleJobToggle(job); }}
                    />
                    <IonLabel>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {job.company}
                        {job.isCancelled && (
                          <IonText color="danger" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '12px', fontWeight: 'normal' }}>
                            <IonIcon icon={banOutline} style={{ fontSize: '14px' }} />
                            Cancelled
                          </IonText>
                        )}
                      </h3>
                      <p style={{ margin: '2px 0' }}>Start: {formatISODateTime(job.dateTimeStart)}</p>
                      <p style={{ margin: '2px 0' }}>Finish: {formatISODateTime(job.dateTimeFinish)}</p>
                      <p>
                        <IonText color="medium">
                          Pay: {job.pay}
                        </IonText>
                      </p>
                    </IonLabel>
                    <IonText slot="end" color="dark">
                      <strong>{formatCurrency(job.estimatedAmount)}</strong>
                    </IonText>
                  </IonItem>
                );
              })}
            </IonList>
          </>
        )}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <div
            style={{
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
          <div>
            <IonText color="medium">
              <p style={{ margin: 0, fontSize: '12px' }}>
                {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
              </p>
            </IonText>
            <IonText>
              <p style={{ margin: '4px 0 0 0', fontWeight: 'bold' }}>
                Estimated Total: {formatCurrency(totalSelected)}
              </p>
            </IonText>
          </div>
          <button
            onClick={handleProceed}
            style={{
              backgroundColor: selectedJobs.length === 0 ? '#888' : '#ff7900',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: selectedJobs.length === 0 ? 'default' : 'pointer',
            }}
          >
            Proceed to Summary
          </button>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};
